<script setup lang="ts">
import { Pet } from "../core/pet";
import { computed } from "vue";
import { Tooltip } from "floating-vue";
import { formatScale } from "../ui/typeScale";
import { calcScale } from "../core/type";

const props = defineProps<{
    leftPet: Pet;
    rightPet: Pet;
}>();

const l2r = computed(() => calcScale(props.leftPet.属性, props.rightPet.属性));
const r2l = computed(() => calcScale(props.rightPet.属性, props.leftPet.属性));

const l2rLabel = computed(() => scaleLabel(l2r.value));
const r2lLabel = computed(() => scaleLabel(r2l.value));
const l2rClass = computed(() => scaleClass(l2r.value));
const r2lClass = computed(() => scaleClass(r2l.value));
const l2rScale = computed(() => formatScale(l2r.value));
const r2lScale = computed(() => formatScale(r2l.value));

function scaleLabel(scale: number): string {
    if (scale > 1) return "克制";
    if (scale === 1) return "普通";
    if (scale === 0) return "无效";
    return "微弱";
}

function scaleClass(scale: number): string {
    if (scale > 1) return "strong";
    if (scale === 1) return "normal";
    if (scale === 0) return "immune";
    return "weak";
}

</script>

<template>
    <div class="restrain-bar">
        <Tooltip :triggers="['hover', 'focus', 'click']">
            <button
                type="button"
                class="tag"
                :class="l2rClass"
                :aria-label="`挑战方攻击被挑战方：${l2rLabel}，${l2rScale}`"
            ><span aria-hidden="true">▶️</span> {{ l2rLabel }}</button>
            <template #popper>{{ l2rScale }}</template>
        </Tooltip>
        <span class="divider" aria-hidden="true">|</span>
        <Tooltip :triggers="['hover', 'focus', 'click']">
            <button
                type="button"
                class="tag"
                :class="r2lClass"
                :aria-label="`被挑战方攻击挑战方：${r2lLabel}，${r2lScale}`"
            >{{ r2lLabel }} <span aria-hidden="true">◀️</span></button>
            <template #popper>{{ r2lScale }}</template>
        </Tooltip>
    </div>
</template>

<style scoped>
.restrain-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(var(--ui-unit) * 6);
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 12);
}

.divider {
    color: #444;
    font-size: calc(var(--ui-unit) * 14);
}

.tag {
    cursor: pointer;
    white-space: nowrap;
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 12);
    border-radius: calc(var(--ui-unit) * 10);
    font-size: calc(var(--ui-unit) * 12);
    font-weight: 700;
    letter-spacing: calc(var(--ui-unit) * 0.5);
    transition: all 0.3s ease;
}

.tag:focus-visible {
    outline: calc(var(--ui-unit) * 2) solid #ffd700;
    outline-offset: calc(var(--ui-unit) * 2);
}

.tag.strong {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: calc(var(--ui-unit) * 1) solid rgba(239, 68, 68, 0.3);
    box-shadow: 0 0 calc(var(--ui-unit) * 8) rgba(239, 68, 68, 0.15);
}

.tag.normal {
    background: rgba(168, 168, 168, 0.12);
    color: #a0a0a0;
    border: calc(var(--ui-unit) * 1) solid rgba(168, 168, 168, 0.25);
}

.tag.immune {
    background: rgba(107, 114, 128, 0.12);
    color: #6b7280;
    border: calc(var(--ui-unit) * 1) solid rgba(107, 114, 128, 0.25);
    text-decoration: line-through;
}

.tag.weak {
    background: rgba(59, 130, 246, 0.12);
    color: #60a5fa;
    border: calc(var(--ui-unit) * 1) solid rgba(59, 130, 246, 0.25);
    box-shadow: 0 0 calc(var(--ui-unit) * 6) rgba(59, 130, 246, 0.08);
}

</style>
